import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
type FormState = {
  name: string;
  email: string;
  company: string;
  interest: string;
  message: string;
};
type FormErrors = Partial<Record<keyof FormState, string>>;
const INTERESTS = ['Brand Licensing', 'Influencer Licensing', 'Retail Drops', 'Manufacturing', 'Other'];
const INPUT_CLASSES = 'w-full border border-white/20 bg-black/40 px-5 py-4 text-sm text-white placeholder:text-white/30 transition-colors focus:border-white focus:outline-none';
export function ContactForm() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const update = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(f => ({
      ...f,
      [field]: e.target.value
    }));
    setErrors(err => ({
      ...err,
      [field]: undefined
    }));
  };
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = 'Name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email address.';
    if (!form.interest) next.interest = 'Select what you\u2019re interested in.';
    if (form.message.trim().length < 10) next.message = 'Tell us a little more (at least 10 characters).';
    setErrors(next);
    if (Object.keys(next).length === 0) setSubmitted(true);
  };
  if (submitted) {
    return <div role="status" className="flex flex-col items-start gap-4 border border-white/20 bg-white/5 p-10" data-id="element-43">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-black" data-id="element-44">
          <CheckIcon className="h-6 w-6" data-id="element-45" />
        </span>
        <p className="font-display text-3xl uppercase tracking-tight text-white sm:text-4xl" data-id="element-46">
          Message Received
        </p>
        <p className="max-w-md text-sm font-light leading-relaxed text-white/55" data-id="element-47">
          Thanks, {form.name.split(' ')[0]}. Our team will reach out at{' '}
          <span className="text-white" data-id="element-48">{form.email}</span> within two business
          days.
        </p>
      </div>;
  }
  return <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6" data-id="element-49">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-50">
        <div data-id="element-51">
          <label htmlFor="contact-name" className="mb-2 block text-xs font-medium uppercase tracking-[0.25em] text-white/50" data-id="element-52">
            Name *
          </label>
          <input id="contact-name" type="text" autoComplete="name" value={form.name} onChange={update('name')} placeholder="Your name" aria-invalid={!!errors.name} className={INPUT_CLASSES} data-id="element-53" />
          {errors.name && <p className="mt-2 text-xs uppercase tracking-wide text-orange-400" data-id="element-54">
              {errors.name}
            </p>}
        </div>
        <div data-id="element-55">
          <label htmlFor="contact-email" className="mb-2 block text-xs font-medium uppercase tracking-[0.25em] text-white/50" data-id="element-56">
            Email *
          </label>
          <input id="contact-email" type="email" autoComplete="email" value={form.email} onChange={update('email')} placeholder="your@email.com" aria-invalid={!!errors.email} className={INPUT_CLASSES} data-id="element-57" />
          {errors.email && <p className="mt-2 text-xs uppercase tracking-wide text-orange-400" data-id="element-58">
              {errors.email}
            </p>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-59">
        <div data-id="element-60">
          <label htmlFor="contact-company" className="mb-2 block text-xs font-medium uppercase tracking-[0.25em] text-white/50" data-id="element-61">
            Brand / Company
          </label>
          <input id="contact-company" type="text" autoComplete="organization" value={form.company} onChange={update('company')} placeholder="Optional" className={INPUT_CLASSES} data-id="element-62" />
        </div>
        <div data-id="element-63">
          <label htmlFor="contact-interest" className="mb-2 block text-xs font-medium uppercase tracking-[0.25em] text-white/50" data-id="element-64">
            I&rsquo;m Interested In *
          </label>
          <select id="contact-interest" value={form.interest} onChange={update('interest')} aria-invalid={!!errors.interest} className={`${INPUT_CLASSES} appearance-none ${form.interest ? 'text-white' : 'text-white/30'}`} data-id="element-65">
            <option value="" disabled className="bg-black text-white/50" data-id="element-66">
              Select one
            </option>
            {INTERESTS.map(i => <option key={i} value={i} className="bg-black text-white" data-id="element-67">
                {i}
              </option>)}
          </select>
          {errors.interest && <p className="mt-2 text-xs uppercase tracking-wide text-orange-400" data-id="element-68">
              {errors.interest}
            </p>}
        </div>
      </div>

      <div data-id="element-69">
        <label htmlFor="contact-message" className="mb-2 block text-xs font-medium uppercase tracking-[0.25em] text-white/50" data-id="element-70">
          Message *
        </label>
        <textarea id="contact-message" rows={5} value={form.message} onChange={update('message')} placeholder="Tell us about your brand, your audience, and where you want to be." aria-invalid={!!errors.message} className={`${INPUT_CLASSES} resize-none`} data-id="element-71" />
        {errors.message && <p className="mt-2 text-xs uppercase tracking-wide text-orange-400" data-id="element-72">
            {errors.message}
          </p>}
      </div>

      <button type="submit" className="group mt-2 inline-flex w-fit items-center gap-10 border border-white/70 px-12 py-5 text-sm font-semibold uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-white hover:text-black" data-id="element-73">
        Send Message
        <ArrowRightIcon className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-74" />
      </button>
    </form>;
}